<template>
  <div class="wrapper-a-b-testing">
    <div class="toggle-label">
      <VSwitch v-model="option" connotation="cta" @change="onToggle" :disabled="selectedTargetingFilters === 'UPLOAD_FILE' || isViewOnlyMode"></VSwitch>
      <span class="toggle-text">Enable split broadcast in 2 parts (A/B Testing)</span>
    </div>
    <template v-if="option">
      <div class="slide-wrapper">
        <div class="left">
          <p class="title ion-no-margin paddingBottom">Version A</p>
          <div class="percentage-wrapper">
            <div class="integer">{{ range }}</div>
            <div class="percentage">%</div>
          </div>
        </div>
        <div class="right">
          <p class="title ion-no-margin paddingBottom">Version B</p>
          <div class="percentage-wrapper">
            <div class="integer">{{ 100 - range }}</div>
            <div class="percentage">%</div>
          </div>
        </div>
      </div>
      <div class="center">
        <VSlider v-model="range" :min="0" :max="100" :disabled="isViewOnlyMode" connotation="cta" aria-label="A/B distribution slider" />
      </div>
    </template>
    <div class="info-box">
      <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
      <p class="info-text ion-no-margin">A/B testing is not available if you are targeting broadcast "by uploading a file with customers"</p>
    </div>
  </div>
</template>

<script>
export default {
  emits:['onUpdate'],
  data() {
    return {
      option: "",
      range: "",
      // Set once the user drags the slider. Prevents the async prop re-hydration
      // on the duplicate flow from reverting the user's split (e.g. 80:20 -> 50:50).
      userChangedRange: false,
      // True while `range` is set programmatically (mount / hydration / toggle
      // default), so the `range` watcher only reacts to real user drags.
      hydrating: false,
    }
  },
  props: {
    selectedOption: {
      type: Boolean,
      default: true
    },
    distributionProbability: {
      type: String,
      default: ""
    },
    selectedTargetingFilters: {
      type: String,
      default: ""
    },
    isViewOnlyMode: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.option = this.selectedOption;
    this.hydrating = true;
    this.range = this.distributionProbability?.split(":")[0] || 50;
    this.$nextTick(() => { this.hydrating = false; });
  },
  watch: {
    selectedOption(newVal) {
      this.option = newVal;
    },
    distributionProbability(newVal) {
      // Don't overwrite an in-progress user selection with a re-hydrated value.
      if (this.userChangedRange) return;
      this.hydrating = true;
      this.range = newVal?.split(":")[0] || 50;
      this.$nextTick(() => { this.hydrating = false; });
    },
    // Vivid's VSlider commits its value via v-model (update:modelValue); its
    // input/change events carry no usable value, so watch `range` for drags.
    range(newVal) {
      const nextRange = Number(newVal);
      if (this.hydrating || !Number.isFinite(nextRange)) return;
      this.userChangedRange = true;
      this.emitUpdate(nextRange);
    }
  },
  methods: {
    emitUpdate(range) {
      this.$emit('onUpdate', {
        range,
        selectedOption: this.option,
        distributionProbability: this.option ? `${range}:${100 - range}` : ''
      });
    },
    onToggle() {
      // Default to 50 when enabling A/B with no prior value.
      let nextRange = Number(this.range);
      if (!Number.isFinite(nextRange) || this.range === '') {
        nextRange = 50;
        this.hydrating = true;
        this.range = 50;
        this.$nextTick(() => { this.hydrating = false; });
      }
      this.emitUpdate(nextRange);
    }
  }
}
</script>

<style scoped>
.wrapper-a-b-testing {
  display: flex;
  flex-direction: column;
  row-gap: 15px;
}
.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  cursor: pointer;
}
.toggle-text {
  color: var(--black);
}
.slide-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.center {
  flex: 1;
  display: flex;
  position: relative;
}
.percentage-wrapper {
  display: flex;
  height: 30px;
  border-radius: 5px;
  width: 100px;
  overflow: hidden;
}
.percentage-wrapper .integer {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 10px;
  background: var(--background-primary);
}
.percentage-wrapper .percentage {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: var(--gray-02);
}
.info-box{
  display: flex;
  align-items: center;
  flex-direction: row;
  background: var(--purple-opac);
  padding: 15px 10px;
  border-radius: 5px;
}
.info-text{
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
  margin-left: 8px;
}
.paddingBottom{
  padding-bottom: 10px;
}
.title {
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--black);
}
</style>
